/**
 * Elite Workflow Templates — HR, DevOps, Data, AI-Powered, Operations, Integrations
 *
 * Workflows 29-47: Production-grade prebuilt templates.
 *
 * DESIGN RULES:
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}

export const ELITE_HR_DEVOPS_DATA_OPS_WORKFLOWS: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // HR (29-32)
  // ===================================================================

  // 29. New Employee Onboarding (9 nodes)
  "New Employee Onboarding": {
    nodes: [
      trigger("Webhook: New hire added to HR system", "webhook_app", {
        source: "BambooHR / Gusto / Rippling",
        event: "employee.created",
        howItStarts: "A new employee is added to your HR system",
        setup:
          "In your HR tool → Integrations → Webhooks → Event: 'New employee' → URL: your workflow URL. The AI will read whatever data your HR system sends and extract what it needs.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the HR data",
        280,
        "Read this HR system webhook data and extract the employee name, employee email, job title, department, start date, manager name, and manager email. Different HR systems send data in different formats — find the employee name, employee email, job title, department, start date, manager name, and manager email regardless of field naming. Respond JSON: {emp_name: string, emp_email: string, emp_title: string, department: string, start_date: string, manager_name: string, manager_email: string}",
        "{webhook_payload}",
        [
          { field: "emp_name", as: "emp_name", type: "string" },
          { field: "emp_email", as: "emp_email", type: "string" },
          { field: "emp_title", as: "emp_title", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "start_date", as: "start_date", type: "string" },
          { field: "manager_name", as: "manager_name", type: "string" },
          { field: "manager_email", as: "manager_email", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Welcome email to new hire",
        520,
        "{emp_email}",
        "Welcome to the team, {emp_name}!",
        "<p>Hi {emp_name},</p><p>We're thrilled to have you join as <strong>{emp_title}</strong> in {department}!</p><p>Your start date: {start_date}</p><p>Your manager: {manager_name}</p><p>We'll send you everything you need before your first day.</p>",
      ),
      slackNode(
        "4",
        "Announce in #team channel",
        520,
        "👋 Welcome {emp_name}! Joining {department} as {emp_title} on {start_date}. Manager: {manager_name}",
        460,
      ),
      n("5", "hitApi", "Create accounts in Okta/Google", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://{okta_domain}.okta.com/api/v1/users?activate=true",
          headers: [
            { key: "Authorization", value: "SSWS {okta_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "profile",
              value:
                '{"firstName":"{emp_name}","email":"{emp_email}","login":"{emp_email}","department":"{department}"}',
            },
          ],
          variables: [
            { name: "id", customName: "okta_user_id", type: "string" },
            { name: "status", customName: "account_status", type: "string" },
          ],
        },
      }),
      n("6", "delayAction", "Wait until start date", 1000, {
        delayAction: { time: 1, timeFormat: "days" },
      }),
      emailNode(
        "7",
        "Day 1: orientation guide",
        1240,
        "{emp_email}",
        "Your Day 1 Guide at {department}",
        "<p>Hi {emp_name},</p><p>Welcome to your first day! Here's everything you need:</p><ul><li>Check your email for login credentials</li><li>Your manager {manager_name} will meet you at 9:00 AM</li><li>Review the team handbook in your inbox</li></ul>",
      ),
      slackNode(
        "8",
        "Remind manager about new hire",
        1240,
        "📋 Reminder: {emp_name} starts today as {emp_title}! Please ensure workstation and accounts are ready. Okta ID: {okta_user_id}",
        460,
      ),
      emailNode(
        "9",
        "IT setup checklist to manager",
        1480,
        "{manager_email}",
        "IT Setup Complete: {emp_name}",
        "<p>Hi {manager_name},</p><p>Accounts for {emp_name} have been provisioned:</p><ul><li>Okta ID: {okta_user_id}</li><li>Status: {account_status}</li><li>Department: {department}</li></ul><p>Please verify access and assign team-specific tools.</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("2", "4"),
      e("2", "5"),
      e("3", "6"),
      e("6", "7"),
      e("6", "8"),
      e("8", "9"),
    ],
    summary: `**New Employee Onboarding** — 9-step automated onboarding!\n\n1. New hire added to HR system\n2. **AI reads the data** and extracts name, email, title, department, start date, manager\n3. Welcome email sent + Slack announcement + Okta account provisioned\n4. On start date: orientation guide + manager reminder + IT checklist\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  // 30. Employee Offboarding (9 nodes)
  "Employee Offboarding": {
    nodes: [
      trigger("Webhook: Employee departure recorded", "webhook_app", {
        source: "BambooHR / Gusto / Rippling",
        event: "employee.terminated",
        howItStarts:
          "Your HR system sends a notification when an employee departure is recorded",
        setup:
          "In your HR tool → Integrations → Webhooks → Event: 'Employee terminated/departed' → URL: your workflow URL. The AI will read whatever data your HR system sends.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the HR webhook",
        280,
        "Read this HR system webhook data and extract the employee name, employee email, department, last working day, manager name, manager email, and reason for departure. Different HR systems send data in different formats — find the employee name, employee email, department, last day, manager name, manager email, and departure reason regardless of field naming. Respond JSON: {emp_name: string, emp_email: string, department: string, last_day: string, manager_name: string, manager_email: string, departure_reason: string}",
        "{webhook_payload}",
        [
          { field: "emp_name", as: "emp_name", type: "string" },
          { field: "emp_email", as: "emp_email", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "last_day", as: "last_day", type: "string" },
          { field: "manager_name", as: "manager_name", type: "string" },
          { field: "manager_email", as: "manager_email", type: "string" },
          { field: "departure_reason", as: "departure_reason", type: "string" },
        ],
      ),
      emailNode(
        "3",
        "Email IT: revoke all access",
        520,
        "it@company.com",
        "Access Revocation Required: {emp_name} — Last day {last_day}",
        "<p>Please revoke all access for <strong>{emp_name}</strong> ({emp_email}) from {department}.</p><p>Last working day: {last_day}</p><p>Checklist:</p><ul><li>Disable email account</li><li>Revoke SSO/Okta access</li><li>Remove from Slack channels</li><li>Collect hardware/badges</li></ul>",
      ),
      emailNode(
        "4",
        "Email employee: exit survey",
        520,
        "{emp_email}",
        "We'd love your feedback, {emp_name}",
        "<p>Hi {emp_name},</p><p>As part of our offboarding process, we'd appreciate your honest feedback. Please complete this brief exit survey: [survey link]</p><p>Your last day with us is {last_day}. Thank you for your contributions to {department}.</p>",
        460,
      ),
      slackNode(
        "5",
        "Notify #hr channel",
        760,
        "📋 Offboarding initiated: {emp_name} ({department}) — Last day: {last_day}\\nReason: {departure_reason}",
      ),
      n(
        "6",
        "hitApi",
        "Disable Okta account",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{okta_domain}.okta.com/api/v1/users/{emp_email}/lifecycle/deactivate",
            headers: [
              { key: "Authorization", value: "SSWS {okta_api_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "status",
                customName: "deactivation_status",
                type: "string",
              },
            ],
          },
        },
        460,
      ),
      n("7", "delayAction", "Wait 1 day", 1000, {
        delayAction: { time: 1, timeFormat: "days" },
      }),
      emailNode(
        "8",
        "Remind manager: exit interview",
        1240,
        "{manager_email}",
        "Reminder: Schedule exit interview with {emp_name}",
        "<p>Hi {manager_name},</p><p>Please schedule an exit interview with {emp_name} before their last day ({last_day}).</p><p>This helps us understand their experience and improve retention. Department: {department}.</p>",
      ),
      emailNode(
        "9",
        "Notify payroll: final paycheck",
        1240,
        "payroll@company.com",
        "Final Paycheck: {emp_name} — {last_day}",
        "<p>Please process final paycheck for {emp_name} ({emp_email}).</p><p>Department: {department}</p><p>Last working day: {last_day}</p><p>Ensure all PTO balances and expenses are settled.</p>",
        460,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("2", "4"),
      e("2", "5"),
      e("2", "6"),
      e("5", "7"),
      e("7", "8"),
      e("7", "9"),
    ],
    summary: `**Employee Offboarding** — 9-step automated exit process!\n\n1. HR system sends departure webhook\n2. **AI reads the data** and extracts employee name, email, department, last day, manager, reason\n3. IT notified to revoke access + Okta account disabled + exit survey sent + #hr notified\n4. 1-day delay: manager reminded for exit interview + payroll notified\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  // 31. Resume Screener (8 nodes)
  "Resume Screener": {
    nodes: [
      trigger("Form: Job application received", "webhook_form", {
        source: "Greenhouse / Lever / Application Form",
        howItStarts: "A candidate submits a job application",
        setup:
          "In your ATS (Greenhouse, Lever) → Webhooks → 'Application submitted' → paste your workflow URL. Or connect a Outgrow Forms application form. The AI will read whatever data your ATS sends.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the application data",
        280,
        "Read this job application webhook/form data and extract the candidate name, candidate email, resume text, job title applied for, job requirements, and recruiter email. Different ATS systems send data in different formats — find the candidate name, candidate email, resume text, job title, job requirements, and recruiter email regardless of field naming. Respond JSON: {candidate_name: string, candidate_email: string, resume_text: string, job_title: string, job_requirements: string, recruiter_email: string}",
        "{webhook_payload}",
        [
          { field: "candidate_name", as: "candidate_name", type: "string" },
          { field: "candidate_email", as: "candidate_email", type: "string" },
          { field: "resume_text", as: "resume_text", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "job_requirements", as: "job_requirements", type: "string" },
          { field: "recruiter_email", as: "recruiter_email", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI score the resume",
        520,
        'Score this resume against the job requirements. Evaluate skills match, experience level, and cultural fit indicators. Respond JSON: {match_score: number, strengths: string, gaps: string, experience_years: number, recommendation: \\"shortlist\\" | \\"maybe\\" | \\"pass\\"}',
        "Job: {job_title}\\nRequirements: {job_requirements}\\nResume: {resume_text}",
        [
          { field: "match_score", as: "match_score", type: "number" },
          { field: "strengths", as: "strengths", type: "string" },
          { field: "gaps", as: "gaps", type: "string" },
          { field: "experience_years", as: "experience_years", type: "number" },
          { field: "recommendation", as: "recommendation", type: "string" },
        ],
      ),
      n("4", "condition", "Should shortlist? (score > 75)", 760, {
        conditionData: [
          {
            variable: "match_score",
            value: "75",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "5",
        "Notify recruiter: strong candidate",
        1000,
        "{recruiter_email}",
        "Strong Candidate: {candidate_name} for {job_title}",
        "<p><strong>Score: {match_score}/100</strong></p><p>Experience: {experience_years} years</p><p>Recommendation: {recommendation}</p><p><strong>Strengths:</strong> {strengths}</p><p><strong>Gaps:</strong> {gaps}</p>",
      ),
      slackNode(
        "6",
        "Alert #hiring on Slack",
        1000,
        "🎯 Strong candidate! {candidate_name} scored {match_score}/100 for {job_title}\\nStrengths: {strengths}",
        460,
      ),
      n("7", "hitApi", "Log to Google Sheets", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Candidates:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{candidate_name}","{candidate_email}","{job_title}","{match_score}","{recommendation}","{strengths}","{gaps}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_added",
              type: "number",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Send rejection: low score",
        1000,
        "{candidate_email}",
        "Update on your application for {job_title}",
        "<p>Hi {candidate_name},</p><p>Thank you for your interest in the {job_title} position. After careful review, we've decided to move forward with other candidates.</p><p>We encourage you to apply for future opportunities that match your experience.</p>",
        840,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "6"),
      e("3", "7"),
      e("4", "8", "output_2"),
    ],
    summary: `**Resume Screener** — AI-powered candidate evaluation!\n\n1. Application received from ATS/form\n2. **AI reads the data** and extracts candidate info, resume, job requirements\n3. AI scores resume 0-100 against requirements\n4. Score > 75 → recruiter notified + Slack alert\n5. Score <= 75 → polite rejection email\n6. All candidates logged to Google Sheets\n\n**To activate:** Say "connect openai", "connect email", "connect slack", "connect google sheets"`,
  },

  // 32. PTO Request (7 nodes)
  "PTO Request Automator": {
    nodes: [
      trigger("Form: PTO request submitted", "webhook_form", {
        source: "Internal PTO Form / Outgrow Forms",
        howItStarts: "An employee submits a PTO request form",
        setup:
          "Create a PTO form (Outgrow Forms, Outgrow Forms) → Webhooks → paste your workflow URL. The AI will read whatever data your form sends.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the PTO form",
        280,
        "Read this PTO request form data and extract the employee name, employee email, start date, end date, number of days requested, reason for leave, and manager email. Different forms send data in different formats — find all fields regardless of naming. Respond JSON: {emp_name: string, emp_email: string, start_date: string, end_date: string, days_requested: number, reason: string, manager_email: string}",
        "{webhook_payload}",
        [
          { field: "emp_name", as: "emp_name", type: "string" },
          { field: "emp_email", as: "emp_email", type: "string" },
          { field: "start_date", as: "start_date", type: "string" },
          { field: "end_date", as: "end_date", type: "string" },
          { field: "days_requested", as: "days_requested", type: "number" },
          { field: "reason", as: "reason", type: "string" },
          { field: "manager_email", as: "manager_email", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI assess PTO request",
        520,
        'Assess this PTO request. Check if the dates seem reasonable, flag if more than 10 days, and check for potential conflicts. Respond JSON: {is_reasonable: \\"true\\" | \\"false\\", conflict_warning: string, recommendation: string, auto_approve: \\"true\\" | \\"false\\"}',
        "Employee: {emp_name}, Start: {start_date}, End: {end_date}, Days: {days_requested}, Reason: {reason}",
        [
          { field: "is_reasonable", as: "is_reasonable", type: "string" },
          { field: "conflict_warning", as: "conflict_warning", type: "string" },
          { field: "recommendation", as: "recommendation", type: "string" },
          { field: "auto_approve", as: "auto_approve", type: "string" },
        ],
      ),
      n("4", "condition", "Auto-approve? (short & no conflicts)", 760, {
        conditionData: [
          {
            variable: "auto_approve",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "5",
        "Auto-approved: notify employee",
        1000,
        "{emp_email}",
        "PTO Approved: {start_date} to {end_date}",
        "<p>Hi {emp_name},</p><p>Your PTO request for {days_requested} days ({start_date} to {end_date}) has been approved.</p><p>Enjoy your time off!</p>",
      ),
      emailNode(
        "6",
        "Route to manager for approval",
        1000,
        "{manager_email}",
        "PTO Request: {emp_name} — {start_date} to {end_date}",
        "<p>{emp_name} has requested {days_requested} days off from {start_date} to {end_date}.</p><p>Reason: {reason}</p><p>AI assessment: {recommendation}</p><p>{conflict_warning}</p>",
        460,
      ),
      slackNode(
        "7",
        "Notify #team calendar",
        1240,
        "📅 PTO: {emp_name} requesting {days_requested} days ({start_date} to {end_date})\\nStatus: {recommendation}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "6", "output_2"),
      e("4", "7"),
    ],
    summary: `**PTO Request Automator** — Smart leave management!\n\n1. Employee submits PTO form\n2. **AI reads the data** and extracts employee info, dates, reason, manager email\n3. AI assesses reasonableness and checks for conflicts\n4. Short requests with no conflicts → auto-approved\n5. Longer/flagged requests → routed to manager\n6. Team calendar updated on Slack\n\n**To activate:** Say "connect openai", "connect email", "connect slack"`,
  },

  // ===================================================================
  // DEVOPS (33-36)
  // ===================================================================

  // 33. Deploy Notification (8 nodes)
  "Deploy Notification Pipeline": {
    nodes: [
      trigger("GitHub: CI/CD pipeline completes", "webhook_app", {
        source: "GitHub Actions",
        event: "workflow_run.completed",
        howItStarts: "Your CI/CD pipeline finishes (success or failure)",
        setup:
          "In GitHub → repo Settings → Webhooks → Add webhook → URL: your workflow URL → Events: 'Workflow runs'. The AI will read whatever data GitHub sends.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the deploy data",
        280,
        "Read this GitHub Actions webhook data and extract the repo name, branch name, commit message, commit author, build result, and run URL. Different CI/CD systems send data in different formats — find all fields regardless of naming. Respond JSON: {repo_name: string, branch_name: string, commit_msg: string, commit_author: string, build_result: string, run_url: string}",
        "{webhook_payload}",
        [
          { field: "repo_name", as: "repo_name", type: "string" },
          { field: "branch_name", as: "branch_name", type: "string" },
          { field: "commit_msg", as: "commit_msg", type: "string" },
          { field: "commit_author", as: "commit_author", type: "string" },
          { field: "build_result", as: "build_result", type: "string" },
          { field: "run_url", as: "run_url", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI summarize deploy risk",
        520,
        'Assess this deployment. Consider if it touches critical files, is a hotfix, or a regular release. Respond JSON: {deploy_status: \\"success\\" | \\"failure\\", summary: string, risk_level: \\"low\\" | \\"medium\\" | \\"high\\", rollback_needed: \\"true\\" | \\"false\\"}',
        "Repo: {repo_name}, Branch: {branch_name}, Commit: {commit_msg}, Author: {commit_author}, Build: {build_result}",
        [
          { field: "deploy_status", as: "deploy_status", type: "string" },
          { field: "summary", as: "deploy_summary", type: "string" },
          { field: "risk_level", as: "risk_level", type: "string" },
          { field: "rollback_needed", as: "rollback_needed", type: "string" },
        ],
      ),
      n("4", "condition", "Did it succeed?", 760, {
        conditionData: [
          {
            variable: "deploy_status",
            value: "success",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "Success → post to #deploys",
        1000,
        "🚀 Deploy succeeded!\\n*{repo_name}* ({branch_name})\\n{deploy_summary}\\nAuthor: {commit_author}\\nRisk: {risk_level}\\n<{run_url}|View run>",
      ),
      slackNode(
        "6",
        "Failure → alert #incidents",
        1000,
        "❌ Deploy FAILED: *{repo_name}* ({branch_name})\\n{deploy_summary}\\nRollback needed: {rollback_needed}\\nAuthor: {commit_author} @oncall\\n<{run_url}|View run>",
        460,
      ),
      emailNode(
        "7",
        "Email release notes (success)",
        1240,
        "engineering@company.com",
        "Deploy: {repo_name} ({branch_name}) — {deploy_status}",
        "<h2>Deployment Summary</h2><p>{deploy_summary}</p><p>Repo: {repo_name}</p><p>Branch: {branch_name}</p><p>Author: {commit_author}</p><p>Risk: {risk_level}</p><p><a href='{run_url}'>View CI/CD Run</a></p>",
      ),
      n(
        "8",
        "hitApi",
        "Log deploy to Google Sheets",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Deploys:append?valueInputOption=RAW",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "values",
                value:
                  '[["{repo_name}","{branch_name}","{commit_author}","{deploy_status}","{risk_level}","{deploy_summary}"]]',
              },
            ],
            variables: [
              {
                name: "updates.updatedRows",
                customName: "rows_added",
                type: "number",
              },
            ],
          },
        },
        460,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "6", "output_2"),
      e("4", "7"),
      e("3", "8"),
    ],
    summary: `**Deploy Notification Pipeline** — Full CI/CD awareness!\n\n1. GitHub Actions webhook fires when pipeline completes\n2. **AI reads the data** and extracts repo, branch, commit, author, build result\n3. AI summarizes deployment and assesses risk\n4. Success → #deploys Slack post + release notes email\n5. Failure → #incidents alert with rollback recommendation\n6. All deploys logged to Google Sheets\n\n**To activate:** Say "connect openai", "connect slack", "connect email", "connect google sheets"`,
  },

  // 34. Error Rate Monitor (8 nodes)
  "Error Rate Monitor": {
    nodes: [
      trigger("Schedule: Every 30 minutes", "schedule", {
        schedule: "Every 30 minutes",
        howItStarts:
          "Checks your error tracking service for spikes every 30 minutes",
        setup:
          "Activate this workflow and say 'connect sentry' to set up your Sentry API token",
        inputFields: [],
      }),
      n("2", "hitApi", "Get error stats from Sentry", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sentry.io/api/0/projects/{org}/{project}/stats/?stat=received&resolution=1h",
          headers: [{ key: "Authorization", value: "Bearer {sentry_token}" }],
          variables: [
            { name: "[0][1]", customName: "error_count", type: "number" },
          ],
        },
      }),
      n("3", "condition", "Spike? (>100 errors/hour)", 520, {
        conditionData: [
          {
            variable: "error_count",
            value: "100",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      n("4", "hitApi", "Get top errors from Sentry", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://sentry.io/api/0/projects/{org}/{project}/issues/?query=is:unresolved&sort=freq&limit=5",
          headers: [{ key: "Authorization", value: "Bearer {sentry_token}" }],
          variables: [
            {
              name: "[0].title",
              customName: "top_error_title",
              type: "string",
            },
            {
              name: "[0].count",
              customName: "top_error_count",
              type: "string",
            },
            {
              name: "[0].permalink",
              customName: "top_error_link",
              type: "string",
            },
          ],
        },
      }),
      aiNode(
        "5",
        "AI assess error severity",
        1000,
        'Assess the severity of this error spike. Consider the error count and top error. Respond JSON: {severity: \\"critical\\" | \\"warning\\" | \\"info\\", assessment: string, suggested_action: string, should_page: \\"true\\" | \\"false\\"}',
        "Error count: {error_count}/hour, Top error: {top_error_title} (count: {top_error_count})",
        [
          { field: "severity", as: "severity", type: "string" },
          { field: "assessment", as: "assessment", type: "string" },
          { field: "suggested_action", as: "suggested_action", type: "string" },
          { field: "should_page", as: "should_page", type: "string" },
        ],
      ),
      n("6", "condition", "Should page on-call?", 1240, {
        conditionData: [
          {
            variable: "should_page",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "7",
        "Page on-call in #incidents",
        1480,
        "🚨 ERROR SPIKE: {error_count} errors/hour!\\nSeverity: {severity}\\nTop error: {top_error_title}\\n{assessment}\\nAction: {suggested_action}\\n<{top_error_link}|View in Sentry> @oncall",
      ),
      emailNode(
        "8",
        "Email on-call engineer",
        1480,
        "oncall@company.com",
        "Error Spike Alert: {error_count} errors/hour",
        "<p><strong>Severity:</strong> {severity}</p><p><strong>Error count:</strong> {error_count}/hour</p><p><strong>Top error:</strong> {top_error_title}</p><p><strong>Assessment:</strong> {assessment}</p><p><strong>Action:</strong> {suggested_action}</p><p><a href='{top_error_link}'>View in Sentry</a></p>",
        460,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "8"),
    ],
    summary: `**Error Rate Monitor** — Intelligent error spike detection!\n\n1. Checks Sentry every 30 minutes\n2. If > 100 errors/hour → fetches top errors\n3. AI assesses severity and suggests action\n4. Critical → pages on-call via Slack + email\n\n**To activate:** Say "connect sentry", "connect openai", "connect slack", "connect email"`,
  },

  // 35. Incident Response (9 nodes)
  "Incident Response Orchestrator": {
    nodes: [
      trigger("Webhook: Monitoring alert fired", "webhook_app", {
        source: "Datadog / PagerDuty / New Relic",
        howItStarts:
          "Your monitoring tool sends an alert when an incident is detected",
        setup:
          "In your monitoring tool (Datadog, PagerDuty, New Relic) → Integrations → Webhooks → paste your workflow URL. The AI will read whatever data your monitoring tool sends.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the monitoring alert",
        280,
        "Read this monitoring alert webhook data and extract the incident type, severity, affected service, description, source system, and metric values. Different monitoring tools send data in different formats — find all fields regardless of naming. Respond JSON: {incident_type: string, severity: string, affected_service: string, description: string, source_system: string, metric_value: string}",
        "{webhook_payload}",
        [
          { field: "incident_type", as: "incident_type", type: "string" },
          { field: "severity", as: "severity", type: "string" },
          { field: "affected_service", as: "affected_service", type: "string" },
          { field: "description", as: "description", type: "string" },
          { field: "source_system", as: "source_system", type: "string" },
          { field: "metric_value", as: "metric_value", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI classify and create runbook",
        520,
        'Classify this incident and generate a response runbook. Respond JSON: {priority: \\"P1\\" | \\"P2\\" | \\"P3\\", is_critical: \\"true\\" | \\"false\\", runbook_steps: string, estimated_impact: string, escalation_needed: \\"true\\" | \\"false\\"}',
        "Type: {incident_type}, Severity: {severity}, Service: {affected_service}, Description: {description}, Source: {source_system}, Metric: {metric_value}",
        [
          { field: "priority", as: "priority", type: "string" },
          { field: "is_critical", as: "is_critical", type: "string" },
          { field: "runbook_steps", as: "runbook_steps", type: "string" },
          { field: "estimated_impact", as: "estimated_impact", type: "string" },
          {
            field: "escalation_needed",
            as: "escalation_needed",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "Is it critical (P1)?", 760, {
        conditionData: [
          {
            variable: "is_critical",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "Critical → alert #incidents",
        1000,
        "🚨 *{priority} INCIDENT: {affected_service}*\\nType: {incident_type}\\nImpact: {estimated_impact}\\nSource: {source_system}\\n\\n*Runbook:*\\n{runbook_steps}\\n\\n@oncall @engineering-lead respond immediately!",
      ),
      emailNode(
        "6",
        "Critical → email on-call + VP Eng",
        1000,
        "oncall@company.com,vp-eng@company.com",
        "P1 INCIDENT: {incident_type} — {affected_service}",
        "<h2>Priority: {priority}</h2><p><strong>Service:</strong> {affected_service}</p><p><strong>Impact:</strong> {estimated_impact}</p><p><strong>Description:</strong> {description}</p><h3>Runbook</h3><p>{runbook_steps}</p>",
        460,
      ),
      slackNode(
        "7",
        "Non-critical → post to #engineering",
        1000,
        "⚠️ {priority} Incident: {incident_type} ({severity})\\nService: {affected_service}\\n{description}\\nRunbook: {runbook_steps}",
        840,
      ),
      n("8", "hitApi", "Create incident in PagerDuty", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.pagerduty.com/incidents",
          headers: [
            { key: "Authorization", value: "Token token={pagerduty_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "incident",
              value:
                '{"type":"incident","title":"{priority}: {incident_type} in {affected_service}","service":{"id":"{pagerduty_service_id}","type":"service_reference"},"urgency":"high","body":{"type":"incident_body","details":"{description}\\n\\nRunbook: {runbook_steps}"}}',
            },
          ],
          variables: [
            {
              name: "incident.id",
              customName: "pagerduty_incident_id",
              type: "string",
            },
          ],
        },
      }),
      n("9", "hitApi", "Log to incident tracker sheet", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Incidents:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{priority}","{incident_type}","{affected_service}","{severity}","{estimated_impact}","{source_system}","{pagerduty_incident_id}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_added",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "6"),
      e("4", "7", "output_2"),
      e("4", "8"),
      e("8", "9"),
    ],
    summary: `**Incident Response Orchestrator** — Full incident lifecycle!\n\n1. Monitoring tool fires alert via webhook\n2. **AI reads the data** and extracts incident details\n3. AI classifies priority and generates runbook\n4. P1 → #incidents Slack + email VP Eng + PagerDuty incident\n5. P2/P3 → #engineering Slack notification\n6. All incidents logged to tracker sheet\n\n**To activate:** Say "connect openai", "connect slack", "connect email", "connect pagerduty", "connect google sheets"`,
  },

  // 36. GitHub PR Alerts (7 nodes)
  "GitHub PR Review Alerts": {
    nodes: [
      trigger("GitHub: Pull request opened", "webhook_app", {
        source: "GitHub",
        event: "pull_request.opened",
        howItStarts: "Someone opens a new pull request",
        setup:
          "In GitHub → repo Settings → Webhooks → Add webhook → URL: your workflow URL → Events: 'Pull requests'. The AI will read whatever data GitHub sends.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the PR data",
        280,
        "Read this GitHub pull request webhook data and extract the PR title, description, author, files changed count, additions, deletions, and PR link. Different GitHub webhook formats vary — find all fields regardless of naming. Respond JSON: {pr_title: string, pr_description: string, pr_author: string, files_changed: number, additions: number, deletions: number, pr_link: string}",
        "{webhook_payload}",
        [
          { field: "pr_title", as: "pr_title", type: "string" },
          { field: "pr_description", as: "pr_description", type: "string" },
          { field: "pr_author", as: "pr_author", type: "string" },
          { field: "files_changed", as: "files_changed", type: "number" },
          { field: "additions", as: "additions", type: "number" },
          { field: "deletions", as: "deletions", type: "number" },
          { field: "pr_link", as: "pr_link", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI summarize and assess risk",
        520,
        'Summarize this pull request in 2-3 sentences. Assess risk based on the number of files changed and the description. Respond JSON: {pr_summary: string, risk: \\"low\\" | \\"medium\\" | \\"high\\", review_priority: \\"urgent\\" | \\"normal\\", suggested_reviewers: string}',
        "PR: {pr_title}\\nDescription: {pr_description}\\nFiles: {files_changed}, +{additions}/-{deletions}",
        [
          { field: "pr_summary", as: "pr_summary", type: "string" },
          { field: "risk", as: "pr_risk", type: "string" },
          { field: "review_priority", as: "review_priority", type: "string" },
          {
            field: "suggested_reviewers",
            as: "suggested_reviewers",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "Is it high risk?", 760, {
        conditionData: [
          {
            variable: "pr_risk",
            value: "high",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "High risk → alert #code-review",
        1000,
        "🔴 High-Risk PR by {pr_author}: *{pr_title}*\\n{pr_summary}\\nFiles: {files_changed} (+{additions}/-{deletions})\\nSuggested reviewers: {suggested_reviewers}\\n<{pr_link}|Review now>",
      ),
      slackNode(
        "6",
        "Normal → post to #engineering",
        1000,
        "🔀 New PR by {pr_author}: *{pr_title}*\\n{pr_summary}\\nRisk: {pr_risk} | Files: {files_changed}\\n<{pr_link}|Review>",
        460,
      ),
      emailNode(
        "7",
        "Email suggested reviewers",
        1240,
        "reviewers@company.com",
        "PR Review Needed: {pr_title}",
        "<p><strong>{pr_author}</strong> opened a {pr_risk}-risk PR.</p><p>{pr_summary}</p><p>Files changed: {files_changed} (+{additions}/-{deletions})</p><p><a href='{pr_link}'>Review on GitHub</a></p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "6", "output_2"),
      e("3", "7"),
    ],
    summary: `**GitHub PR Review Alerts** — Smart PR routing!\n\n1. PR opened webhook fires from GitHub\n2. **AI reads the data** and extracts PR title, description, author, changes\n3. AI summarizes + assesses risk + suggests reviewers\n4. High risk → #code-review Slack alert\n5. Normal risk → #engineering post\n6. All PRs → email notification to reviewers\n\n**To activate:** Say "connect openai", "connect slack", "connect email"`,
  },

  // ===================================================================
  // DATA (37-39)
  // ===================================================================

  // 37. Daily Report Generator (8 nodes)
  "Daily Report Generator": {
    nodes: [
      trigger("Schedule: Every day at 8:00 AM", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Generates a comprehensive daily business report every morning",
        setup:
          "Activate this workflow. Connect your data sources (Stripe for revenue, helpdesk for tickets, analytics for traffic)",
        inputFields: [],
      }),
      n("2", "hitApi", "Fetch revenue from Stripe", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.stripe.com/v1/balance",
          headers: [{ key: "Authorization", value: "Bearer {stripe_key}" }],
          variables: [
            {
              name: "available[0].amount",
              customName: "available_balance",
              type: "number",
            },
            {
              name: "pending[0].amount",
              customName: "pending_balance",
              type: "number",
            },
          ],
        },
      }),
      n(
        "3",
        "hitApi",
        "Fetch support ticket stats",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.your-helpdesk.com/v2/tickets?status=open",
            headers: [
              { key: "Authorization", value: "Bearer {helpdesk_api_key}" },
            ],
            variables: [
              { name: "count", customName: "open_tickets", type: "number" },
            ],
          },
        },
        460,
      ),
      aiNode(
        "4",
        "AI generate executive summary",
        520,
        'Generate a daily business report. Analyze the revenue data and support metrics. Respond JSON: {executive_summary: string, highlights: string, action_items: string, health_score: number, trend: \\"up\\" | \\"stable\\" | \\"down\\"}',
        "Revenue balance: ${available_balance} (pending: ${pending_balance}), Open support tickets: {open_tickets}",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "highlights", as: "highlights", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "health_score", as: "health_score", type: "number" },
          { field: "trend", as: "trend", type: "string" },
        ],
      ),
      n("5", "condition", "Business health declining?", 760, {
        conditionData: [
          {
            variable: "health_score",
            value: "60",
            comparison_operator: "LESS THAN",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "6",
        "Email daily report to leadership",
        1000,
        "leadership@company.com",
        "Daily Business Report — Health: {health_score}/100 ({trend})",
        "<h2>Executive Summary</h2><p>{executive_summary}</p><h3>Highlights</h3><p>{highlights}</p><h3>Action Items</h3><p>{action_items}</p><p>Health Score: {health_score}/100 | Trend: {trend}</p>",
      ),
      slackNode(
        "7",
        "Post summary to #leadership",
        1000,
        "📊 Daily Report — Health: {health_score}/100 ({trend})\\n{executive_summary}\\n\\nAction items: {action_items}",
        460,
      ),
      slackNode(
        "8",
        "Alert: health declining",
        1240,
        "⚠️ Business health declining! Score: {health_score}/100\\n{executive_summary}\\nAction items: {action_items}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("1", "3"),
      e("2", "4"),
      e("3", "4"),
      e("4", "5"),
      e("4", "6"),
      e("4", "7"),
      e("5", "8"),
    ],
    summary: `**Daily Report Generator** — Multi-source business intelligence!\n\n1. Runs every morning at 8 AM\n2. Fetches revenue from Stripe + ticket stats from helpdesk\n3. AI generates executive summary with health score\n4. Report emailed to leadership + posted to Slack\n5. If health declining → extra alert\n\n**To activate:** Say "connect stripe", "connect openai", "connect email", "connect slack"`,
  },

  // 38. Sheets to CRM Sync (8 nodes)
  "Sheets to CRM Sync": {
    nodes: [
      trigger("Google Sheets: New row added", "webhook_app", {
        source: "Google Sheets",
        event: "row.created",
        howItStarts: "A new row is added to your Google Sheet",
        setup:
          "Use Google Apps Script or Zapier to POST to your workflow URL when a new row is added. The AI will read whatever data Google Sheets sends.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the row data",
        280,
        "Read this Google Sheets webhook data and extract the contact email, first name, last name, company, phone number, and lead source. Different sheets send data in different formats — find all fields regardless of naming. Respond JSON: {contact_email: string, first_name: string, last_name: string, company: string, phone: string, lead_source: string}",
        "{webhook_payload}",
        [
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "last_name", as: "last_name", type: "string" },
          { field: "company", as: "company", type: "string" },
          { field: "phone", as: "phone", type: "string" },
          { field: "lead_source", as: "lead_source", type: "string" },
        ],
      ),
      n("3", "hitApi", "Check if contact exists in HubSpot", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{contact_email}"}]}]',
            },
          ],
          variables: [
            { name: "total", customName: "existing_count", type: "number" },
          ],
        },
      }),
      n("4", "condition", "Contact already exists?", 760, {
        conditionData: [
          {
            variable: "existing_count",
            value: "0",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      n("5", "hitApi", "Create new HubSpot contact", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"email":"{contact_email}","firstname":"{first_name}","lastname":"{last_name}","company":"{company}","phone":"{phone}","hs_lead_status":"NEW","leadsource":"{lead_source}"}',
            },
          ],
          variables: [
            { name: "id", customName: "hubspot_contact_id", type: "string" },
          ],
        },
      }),
      slackNode(
        "6",
        "Confirm new contact synced",
        1240,
        "✅ New contact synced to HubSpot: {first_name} {last_name} ({contact_email})\\nCompany: {company} | Source: {lead_source}\\nHubSpot ID: {hubspot_contact_id}",
      ),
      slackNode(
        "7",
        "Duplicate detected: skip",
        1000,
        "⚠️ Duplicate skipped: {first_name} {last_name} ({contact_email}) already exists in HubSpot",
        460,
      ),
      emailNode(
        "8",
        "Welcome email to new contact",
        1240,
        "{contact_email}",
        "Welcome, {first_name}!",
        "<p>Hi {first_name},</p><p>Thanks for connecting with us! We'll be in touch soon with more information about how we can help {company}.</p>",
        460,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("4", "7", "output_2"),
      e("5", "8"),
    ],
    summary: `**Sheets to CRM Sync** — Dedup-aware data pipeline!\n\n1. New row added to Google Sheets\n2. **AI reads the data** and extracts contact info\n3. Checks HubSpot for duplicates\n4. New contact → creates in HubSpot + Slack confirmation + welcome email\n5. Duplicate → skipped with Slack notification\n\n**To activate:** Say "connect openai", "connect hubspot", "connect slack", "connect email"`,
  },

  // 39. Data Dedup Pipeline (9 nodes)
  "Data Dedup Pipeline": {
    nodes: [
      trigger("Schedule: Every day at 6:00 AM", "schedule", {
        schedule: "Every day at 6:00 AM",
        howItStarts: "Scans your CRM daily for duplicate contacts",
        setup:
          "Activate this workflow and say 'connect hubspot' to set up your HubSpot API token",
        inputFields: [],
      }),
      n("2", "hitApi", "Fetch recent contacts from HubSpot", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts?limit=100&properties=email,firstname,lastname,company,phone,createdate&sorts=-createdate",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          variables: [
            { name: "results", customName: "recent_contacts", type: "array" },
            { name: "total", customName: "total_contacts", type: "number" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI detect duplicates",
        520,
        "Analyze these contacts and find duplicates based on email, name similarity, and company match. Group duplicates together. Respond JSON: {duplicate_groups: string, duplicate_count: number, total_scanned: number, merge_recommendations: string, confidence: number}",
        "Contacts: {recent_contacts}",
        [
          { field: "duplicate_groups", as: "duplicate_groups", type: "string" },
          { field: "duplicate_count", as: "duplicate_count", type: "number" },
          { field: "total_scanned", as: "total_scanned", type: "number" },
          {
            field: "merge_recommendations",
            as: "merge_recommendations",
            type: "string",
          },
          { field: "confidence", as: "confidence", type: "number" },
        ],
      ),
      n("4", "condition", "Duplicates found?", 760, {
        conditionData: [
          {
            variable: "duplicate_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      n("5", "condition", "High confidence? (>85%)", 1000, {
        conditionData: [
          {
            variable: "confidence",
            value: "85",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      n("6", "approvalGate", "Review before merging", 1240, {
        approvalGateData: {
          message:
            "Found {duplicate_count} duplicate groups. Review merge recommendations:\\n{merge_recommendations}",
        },
      }),
      slackNode(
        "7",
        "Alert: duplicates for review",
        1480,
        "🔄 Data Dedup Report:\\n{duplicate_count} duplicate groups found (scanned {total_scanned})\\nConfidence: {confidence}%\\n\\n{merge_recommendations}",
      ),
      emailNode(
        "8",
        "Email dedup report",
        1480,
        "data-ops@company.com",
        "Dedup Report: {duplicate_count} duplicates found",
        "<h2>Duplicate Detection Report</h2><p>Scanned: {total_scanned} contacts</p><p>Duplicates found: {duplicate_count} groups</p><p>Confidence: {confidence}%</p><h3>Merge Recommendations</h3><p>{merge_recommendations}</p><h3>Duplicate Groups</h3><p>{duplicate_groups}</p>",
        460,
      ),
      n("9", "hitApi", "Log dedup results to Sheets", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/DedupLog:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{total_scanned}","{duplicate_count}","{confidence}","{merge_recommendations}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_added",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "8"),
      e("4", "9"),
    ],
    summary: `**Data Dedup Pipeline** — Automated CRM hygiene!\n\n1. Daily scan of HubSpot contacts at 6 AM\n2. AI detects duplicates by email, name similarity, company match\n3. High confidence duplicates → approval gate for merge review\n4. Slack alert + email report with merge recommendations\n5. All dedup results logged to Google Sheets\n\n**To activate:** Say "connect hubspot", "connect openai", "connect slack", "connect email", "connect google sheets"`,
  },

  // ===================================================================
  // AI-POWERED (40-42)
  // ===================================================================

  // 40. AI Customer Response (10 nodes)
  "AI Customer Response": {
    nodes: [
      trigger("Email: Support request received", "email", {
        howItStarts: "A customer sends a support email",
        setup:
          "Forward support emails to your workflow inbox, or connect your helpdesk (Zendesk, Freshdesk) webhook",
        inputFields: [
          { name: "customer_email", type: "string" },
          { name: "subject", type: "string" },
          { name: "message_body", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI classify the request",
        280,
        'Classify this support request by category and urgency. Respond JSON: {category: string, urgency: \\"critical\\" | \\"high\\" | \\"normal\\" | \\"low\\", language: string, sentiment: string}',
        "From: {customer_email}\\nSubject: {subject}\\nMessage: {message_body}",
        [
          { field: "category", as: "category", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "language", as: "language", type: "string" },
          { field: "sentiment", as: "sentiment", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI draft a response",
        520,
        'Draft a helpful, empathetic support response. Use the category to guide your answer. Match the customer\'s language if not English. Respond JSON: {response_text: string, confidence: number, needs_human: \\"true\\" | \\"false\\", internal_notes: string}',
        "Category: {category}, Urgency: {urgency}, Language: {language}\\nFrom: {customer_email}\\nSubject: {subject}\\nMessage: {message_body}",
        [
          { field: "response_text", as: "response_text", type: "string" },
          { field: "confidence", as: "confidence", type: "number" },
          { field: "needs_human", as: "needs_human", type: "string" },
          { field: "internal_notes", as: "internal_notes", type: "string" },
        ],
      ),
      n("4", "condition", "AI confident enough? (>80%)", 760, {
        conditionData: [
          {
            variable: "confidence",
            value: "80",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      n(
        "5",
        "condition",
        "Is it critical urgency?",
        760,
        {
          conditionData: [
            {
              variable: "urgency",
              value: "critical",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
        460,
      ),
      emailNode(
        "6",
        "Auto-send response to customer",
        1000,
        "{customer_email}",
        "Re: {subject}",
        "<p>{response_text}</p>",
      ),
      slackNode(
        "7",
        "Log auto-response to #support",
        1000,
        "🤖 Auto-responded to {customer_email}\\nCategory: {category} | Confidence: {confidence}%\\nSubject: {subject}",
        460,
      ),
      slackNode(
        "8",
        "Escalate to human on Slack",
        1000,
        "⚠️ Needs human review (confidence: {confidence}%)\\nFrom: {customer_email}\\nCategory: {category} | Urgency: {urgency}\\nSentiment: {sentiment}\\n\\n{message_body}\\n\\nAI draft: {response_text}\\nNotes: {internal_notes}",
        840,
      ),
      slackNode(
        "9",
        "Critical → page on-call",
        1240,
        "🚨 CRITICAL support request from {customer_email}!\\nSubject: {subject}\\nSentiment: {sentiment}\\n@support-lead respond immediately!",
        460,
      ),
      n("10", "hitApi", "Log to support tracker", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Support:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{customer_email}","{subject}","{category}","{urgency}","{confidence}","{sentiment}","{needs_human}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_added",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("3", "5"),
      e("4", "6"),
      e("4", "7"),
      e("4", "8", "output_2"),
      e("5", "9"),
      e("3", "10"),
    ],
    summary: `**AI Customer Response** — 10-step intelligent support!\n\n1. Support email arrives\n2. AI classifies category, urgency, language, sentiment\n3. AI drafts empathetic response with confidence score\n4. Confidence > 80% → auto-sends to customer + logs\n5. Low confidence → escalates to human on Slack\n6. Critical urgency → pages support lead\n7. All tickets logged to tracker\n\n**To activate:** Say "connect openai", "connect email", "connect slack", "connect google sheets"`,
  },

  // 41. AI Research Assistant (9 nodes)
  "AI Research Assistant": {
    nodes: [
      trigger("Manual: Submit research topic", "manual", {
        howItStarts: "You type a research topic in the chat",
        setup:
          "Type your question in the chat (e.g., 'What are the latest trends in AI?') or POST via API",
        inputFields: [
          {
            name: "topic",
            type: "string",
            example: "AI trends in healthcare 2025",
          },
          { name: "requester_email", type: "string" },
          { name: "depth", type: "string", example: "deep" },
        ],
      }),
      n("2", "hitApi", "Search the web via SerpAPI", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search?q={topic}&num=10",
          headers: [],
          params: [{ key: "api_key", value: "{serpapi_key}" }],
          variables: [
            {
              name: "organic_results",
              customName: "search_results",
              type: "array",
            },
            {
              name: "search_information.total_results",
              customName: "total_results",
              type: "number",
            },
          ],
        },
      }),
      n(
        "3",
        "hitApi",
        "Search academic papers",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.semanticscholar.org/graph/v1/paper/search?query={topic}&limit=5&fields=title,abstract,year,citationCount,url",
            headers: [],
            variables: [
              { name: "data", customName: "academic_papers", type: "array" },
              { name: "total", customName: "paper_count", type: "number" },
            ],
          },
        },
        460,
      ),
      aiNode(
        "4",
        "AI synthesize all findings",
        520,
        "Synthesize these web results and academic papers into a comprehensive research brief. Include key findings, conflicting viewpoints, and confidence level. Respond JSON: {summary: string, key_findings: string, source_count: number, confidence_level: string, gaps_in_research: string, recommendations: string}",
        "Topic: {topic}\\nWeb Results: {search_results}\\nAcademic Papers: {academic_papers}",
        [
          { field: "summary", as: "research_summary", type: "string" },
          { field: "key_findings", as: "key_findings", type: "string" },
          { field: "source_count", as: "source_count", type: "number" },
          { field: "confidence_level", as: "confidence_level", type: "string" },
          { field: "gaps_in_research", as: "gaps_in_research", type: "string" },
          { field: "recommendations", as: "recommendations", type: "string" },
        ],
      ),
      aiNode(
        "5",
        "AI generate executive brief",
        760,
        "Create a 1-paragraph executive brief suitable for a CEO or board. Keep it under 100 words. Respond JSON: {executive_brief: string, one_liner: string}",
        "Research summary: {research_summary}\\nKey findings: {key_findings}\\nRecommendations: {recommendations}",
        [
          { field: "executive_brief", as: "executive_brief", type: "string" },
          { field: "one_liner", as: "one_liner", type: "string" },
        ],
      ),
      emailNode(
        "6",
        "Email full research report",
        1000,
        "{requester_email}",
        "Research Report: {topic}",
        "<h2>{topic}</h2><h3>Executive Brief</h3><p>{executive_brief}</p><h3>Full Summary</h3><p>{research_summary}</p><h3>Key Findings</h3><p>{key_findings}</p><h3>Gaps in Research</h3><p>{gaps_in_research}</p><h3>Recommendations</h3><p>{recommendations}</p><p>Sources analyzed: {source_count} | Confidence: {confidence_level}</p>",
      ),
      slackNode(
        "7",
        "Post brief to #research",
        1000,
        "🔬 Research complete: *{topic}*\\n{one_liner}\\n\\nSources: {source_count} | Confidence: {confidence_level}\\nFull report emailed to {requester_email}",
        460,
      ),
      n("8", "hitApi", "Save to research library (Sheets)", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Research:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{topic}","{one_liner}","{source_count}","{confidence_level}","{recommendations}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_added",
              type: "number",
            },
          ],
        },
      }),
      n(
        "9",
        "condition",
        "Low confidence?",
        1240,
        {
          conditionData: [
            {
              variable: "confidence_level",
              value: "low",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
        460,
      ),
    ],
    edges: [
      e("1", "2"),
      e("1", "3"),
      e("2", "4"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("5", "7"),
      e("5", "8"),
      e("5", "9"),
    ],
    summary: `**AI Research Assistant** — Multi-source deep research!\n\n1. Submit a research topic\n2. Searches web (SerpAPI) + academic papers (Semantic Scholar) in parallel\n3. AI synthesizes all findings into comprehensive brief\n4. AI generates executive summary\n5. Full report emailed + brief posted to Slack + saved to library\n\n**To activate:** Say "connect serpapi", "connect openai", "connect email", "connect slack", "connect google sheets"`,
  },

  // 42. AI Meeting Notes (8 nodes)
  "AI Meeting Notes Processor": {
    nodes: [
      trigger("Manual: Submit meeting transcript", "manual", {
        howItStarts: "You paste a meeting transcript for AI analysis",
        setup:
          "Paste the transcript in chat, or POST via API. You can also connect Zoom/Teams for auto-transcription",
        inputFields: [
          { name: "transcript_text", type: "string" },
          { name: "meeting_type", type: "string", example: "standup" },
          { name: "attendees_email", type: "string" },
        ],
      }),
      aiNode(
        "2",
        "AI extract structured notes",
        280,
        "Extract structured notes from this meeting transcript. Identify participants, decisions, action items with owners, and follow-ups. Respond JSON: {meeting_title: string, participants: string, notes_summary: string, decisions_text: string, action_items_text: string, follow_ups: string, meeting_duration_estimate: string}",
        "Meeting type: {meeting_type}\\nTranscript: {transcript_text}",
        [
          { field: "meeting_title", as: "meeting_title", type: "string" },
          { field: "participants", as: "participants", type: "string" },
          { field: "notes_summary", as: "notes_summary", type: "string" },
          { field: "decisions_text", as: "decisions_text", type: "string" },
          {
            field: "action_items_text",
            as: "action_items_text",
            type: "string",
          },
          { field: "follow_ups", as: "follow_ups", type: "string" },
          {
            field: "meeting_duration_estimate",
            as: "meeting_duration",
            type: "string",
          },
        ],
      ),
      aiNode(
        "3",
        "AI prioritize action items",
        520,
        'Prioritize these action items by urgency and impact. Identify any blockers. Respond JSON: {priority_actions: string, blocker_count: number, has_blockers: \\"true\\" | \\"false\\", deadline_summary: string}',
        "Action items: {action_items_text}\\nDecisions: {decisions_text}\\nFollowups: {follow_ups}",
        [
          { field: "priority_actions", as: "priority_actions", type: "string" },
          { field: "blocker_count", as: "blocker_count", type: "number" },
          { field: "has_blockers", as: "has_blockers", type: "string" },
          { field: "deadline_summary", as: "deadline_summary", type: "string" },
        ],
      ),
      n("4", "condition", "Any blockers identified?", 760, {
        conditionData: [
          {
            variable: "has_blockers",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "5",
        "Email meeting summary to attendees",
        1000,
        "{attendees_email}",
        "Meeting Notes: {meeting_title}",
        "<h2>{meeting_title}</h2><p>Participants: {participants}</p><p>Duration: ~{meeting_duration}</p><h3>Summary</h3><p>{notes_summary}</p><h3>Decisions</h3><p>{decisions_text}</p><h3>Action Items (Prioritized)</h3><p>{priority_actions}</p><h3>Deadlines</h3><p>{deadline_summary}</p><h3>Follow-ups</h3><p>{follow_ups}</p>",
      ),
      slackNode(
        "6",
        "Post action items to Slack",
        1000,
        "📝 *{meeting_title}*\\n{priority_actions}\\n\\nDeadlines: {deadline_summary}",
        460,
      ),
      slackNode(
        "7",
        "Alert: blockers need attention",
        1240,
        "🚧 Blockers from *{meeting_title}*!\\n{blocker_count} blocker(s) identified:\\n{priority_actions}\\n\\nPlease resolve ASAP",
      ),
      n(
        "8",
        "hitApi",
        "Archive to Google Sheets",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Meetings:append?valueInputOption=RAW",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "values",
                value:
                  '[["{meeting_title}","{participants}","{notes_summary}","{action_items_text}","{blocker_count}"]]',
              },
            ],
            variables: [
              {
                name: "updates.updatedRows",
                customName: "rows_added",
                type: "number",
              },
            ],
          },
        },
        460,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("3", "5"),
      e("3", "6"),
      e("4", "7"),
      e("3", "8"),
    ],
    summary: `**AI Meeting Notes Processor** — Smart meeting intelligence!\n\n1. Submit meeting transcript\n2. AI extracts structured notes: participants, decisions, action items, follow-ups\n3. AI prioritizes action items and identifies blockers\n4. Summary emailed to attendees + action items posted to Slack\n5. Blockers → extra Slack alert\n6. Archived to Google Sheets\n\n**To activate:** Say "connect openai", "connect email", "connect slack", "connect google sheets"`,
  },

  // ===================================================================
  // OPERATIONS (43-45)
  // ===================================================================

  // 43. Approval Workflow (8 nodes)
  "Multi-Level Approval Workflow": {
    nodes: [
      trigger("Form: Approval request submitted", "webhook_form", {
        source: "Internal Form / Slack / Custom App",
        howItStarts: "Someone submits a request that needs approval",
        setup:
          "Set up an internal form or Slack workflow that POSTs to your workflow URL with request details. The AI will read whatever data your form sends.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads the request data",
        280,
        "Read this form/webhook data and extract the requester name, requester email, request title, request description, request amount (if any), and approver email. Different forms send data in different formats — find all fields regardless of naming. Respond JSON: {requester_name: string, requester_email: string, request_title: string, request_description: string, request_amount: number, approver_email: string}",
        "{webhook_payload}",
        [
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "request_title", as: "request_title", type: "string" },
          {
            field: "request_description",
            as: "request_description",
            type: "string",
          },
          { field: "request_amount", as: "request_amount", type: "number" },
          { field: "approver_email", as: "approver_email", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI assess and recommend",
        520,
        'Assess this request. Determine if it seems reasonable, flag any concerns, and provide a recommendation. Respond JSON: {risk_assessment: string, recommendation: \\"approve\\" | \\"review\\" | \\"reject\\", reasoning: string, needs_escalation: \\"true\\" | \\"false\\"}',
        "Requester: {requester_name}, Title: {request_title}, Description: {request_description}, Amount: ${request_amount}",
        [
          { field: "risk_assessment", as: "risk_assessment", type: "string" },
          { field: "recommendation", as: "recommendation", type: "string" },
          { field: "reasoning", as: "reasoning", type: "string" },
          { field: "needs_escalation", as: "needs_escalation", type: "string" },
        ],
      ),
      emailNode(
        "4",
        "Notify approver",
        760,
        "{approver_email}",
        "Approval Needed: {request_title} (${request_amount})",
        "<p><strong>{requester_name}</strong> submitted:</p><p>{request_description}</p><p>Amount: ${request_amount}</p><p><strong>AI Assessment:</strong> {risk_assessment}</p><p><strong>Recommendation:</strong> {recommendation} — {reasoning}</p>",
      ),
      n("5", "approvalGate", "Wait for approval decision", 1000, {
        approvalGateData: {
          message:
            "Request from {requester_name}: {request_title} (${request_amount})\\nAI recommends: {recommendation}",
        },
      }),
      emailNode(
        "6",
        "Notify requester: approved",
        1240,
        "{requester_email}",
        "Approved: {request_title}",
        "<p>Good news, {requester_name}! Your request for <strong>{request_title}</strong> (${request_amount}) has been approved.</p>",
      ),
      emailNode(
        "7",
        "Notify requester: rejected",
        1240,
        "{requester_email}",
        "Update on: {request_title}",
        "<p>Hi {requester_name},</p><p>Unfortunately, your request for {request_title} (${request_amount}) was not approved at this time.</p><p>Reason: {reasoning}</p>",
        460,
      ),
      slackNode(
        "8",
        "Log decision to #approvals",
        1480,
        "📋 Approval decision for {request_title} (${request_amount}) by {requester_name}\\nAI recommendation was: {recommendation}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("5", "7", "output_2"),
      e("5", "8"),
    ],
    summary: `**Multi-Level Approval Workflow** — AI-assisted decision making!\n\n1. Request submitted via form\n2. **AI reads the data** and extracts requester info, description, amount\n3. AI assesses risk and provides recommendation\n4. Approver emailed with AI assessment\n5. Human approves/rejects via approval gate\n6. Requester notified of outcome + logged to Slack\n\n**To activate:** Say "connect openai" and "connect email" and "connect slack"`,
  },

  // 44. Webhook Event Router (8 nodes)
  "Webhook Event Router": {
    nodes: [
      trigger("Webhook: Any app event", "webhook_app", {
        source: "Any App",
        howItStarts: "Any external app sends an event to your webhook URL",
        setup:
          "Copy your workflow webhook URL and paste it in ANY app that sends events. The AI will automatically classify and route it",
        inputFields: [{ name: "webhook_payload", type: "object" }],
      }),
      aiNode(
        "2",
        "AI classify and route event",
        280,
        'Classify this webhook event. Determine the source app, event type, priority, correct team routing, and extract a summary. Respond JSON: {source_app: string, event_type: string, priority: \\"critical\\" | \\"high\\" | \\"normal\\" | \\"low\\", route_to: \\"sales\\" | \\"engineering\\" | \\"support\\" | \\"ops\\" | \\"finance\\", event_summary: string, requires_action: \\"true\\" | \\"false\\"}',
        "{webhook_payload}",
        [
          { field: "source_app", as: "source_app", type: "string" },
          { field: "event_type", as: "event_type", type: "string" },
          { field: "priority", as: "priority", type: "string" },
          { field: "route_to", as: "route_to", type: "string" },
          { field: "event_summary", as: "event_summary", type: "string" },
          { field: "requires_action", as: "requires_action", type: "string" },
        ],
      ),
      n("3", "condition", "Route to sales?", 520, {
        conditionData: [
          {
            variable: "route_to",
            value: "sales",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "4",
        "Post to #sales",
        760,
        "🎯 [{source_app}] {event_type}: {event_summary}\\nPriority: {priority}",
      ),
      n(
        "5",
        "condition",
        "Route to engineering?",
        760,
        {
          conditionData: [
            {
              variable: "route_to",
              value: "engineering",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
        460,
      ),
      slackNode(
        "6",
        "Post to #engineering",
        1000,
        "⚡ [{source_app}] {event_type}: {event_summary}\\nPriority: {priority}",
        460,
      ),
      slackNode(
        "7",
        "Post to #support or #ops or #finance",
        1000,
        "📋 [{source_app}] {event_type} → {route_to}\\n{event_summary}\\nPriority: {priority}\\nAction required: {requires_action}",
        840,
      ),
      n("8", "hitApi", "Log all events to Sheets", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Events:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{source_app}","{event_type}","{priority}","{route_to}","{event_summary}","{requires_action}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_added",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("3", "5", "output_2"),
      e("5", "6"),
      e("5", "7", "output_2"),
      e("2", "8"),
    ],
    summary: `**Webhook Event Router** — Universal event dispatcher!\n\n1. Any app sends an event to your webhook\n2. AI reads the payload and classifies it (source, type, priority, team)\n3. Routes to the correct Slack channel: #sales, #engineering, #support, #ops, or #finance\n4. All events logged to Google Sheets\n\nNo hardcoded event types — AI handles ANY payload.\n\n**To activate:** Say "connect openai", "connect slack", "connect google sheets"`,
  },

  // 45. SLA Monitor (8 nodes)
  "SLA Monitor": {
    nodes: [
      trigger("Schedule: Every hour", "schedule", {
        schedule: "Every hour",
        howItStarts:
          "Checks your helpdesk for tickets exceeding SLA every hour",
        setup:
          "Activate this workflow and configure your helpdesk API endpoint in the next node",
        inputFields: [],
      }),
      n("2", "hitApi", "Get open tickets from helpdesk", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.your-helpdesk.com/v2/tickets?status=open&sort_by=created_at",
          headers: [
            { key: "Authorization", value: "Bearer {helpdesk_api_key}" },
          ],
          variables: [
            { name: "tickets", customName: "open_tickets", type: "array" },
            { name: "count", customName: "ticket_count", type: "number" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI analyze SLA breaches",
        520,
        "Analyze these open tickets and find any that have exceeded their SLA. For each breached ticket, calculate how long it has been overdue and assess impact. Respond JSON: {breached_count: number, critical_tickets: string, warning_tickets: string, sla_summary: string, avg_breach_hours: number}",
        "Open tickets: {open_tickets}, Total: {ticket_count}",
        [
          { field: "breached_count", as: "breached_count", type: "number" },
          { field: "critical_tickets", as: "critical_tickets", type: "string" },
          { field: "warning_tickets", as: "warning_tickets", type: "string" },
          { field: "sla_summary", as: "sla_summary", type: "string" },
          { field: "avg_breach_hours", as: "avg_breach_hours", type: "number" },
        ],
      ),
      n("4", "condition", "Any SLA breaches?", 760, {
        conditionData: [
          {
            variable: "breached_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      n("5", "condition", "Average breach > 4 hours?", 1000, {
        conditionData: [
          {
            variable: "avg_breach_hours",
            value: "4",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "6",
        "Alert #support",
        1240,
        "🚨 SLA Breach: {breached_count} tickets over SLA!\\nAvg overdue: {avg_breach_hours}h\\n\\nCritical: {critical_tickets}\\nWarning: {warning_tickets}\\n\\n{sla_summary}",
      ),
      emailNode(
        "7",
        "Email support lead",
        1240,
        "support-lead@company.com",
        "SLA Alert: {breached_count} tickets breached (avg {avg_breach_hours}h overdue)",
        "<h2>SLA Breach Alert</h2><p>{breached_count} tickets have exceeded SLA (avg {avg_breach_hours}h overdue).</p><h3>Critical Tickets</h3><p>{critical_tickets}</p><h3>Warning Tickets</h3><p>{warning_tickets}</p><p>{sla_summary}</p>",
        460,
      ),
      emailNode(
        "8",
        "Escalate to VP Support",
        1480,
        "vp-support@company.com",
        "ESCALATION: SLA breach avg {avg_breach_hours}h",
        "<p><strong>Escalation:</strong> Average SLA breach time exceeds 4 hours.</p><p>{breached_count} tickets affected.</p><p>{sla_summary}</p><p>Critical tickets: {critical_tickets}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "6"),
      e("4", "7"),
      e("5", "8"),
    ],
    summary: `**SLA Monitor** — Multi-tier SLA enforcement!\n\n1. Checks helpdesk every hour for open tickets\n2. AI identifies SLA breaches with overdue time and impact\n3. Any breach → Slack alert + email to support lead\n4. Average breach > 4 hours → escalates to VP Support\n\n**To activate:** Say "connect openai", "connect slack", "connect email"`,
  },

  // ===================================================================
  // INTEGRATIONS (46-47)
  // ===================================================================

  // 46. Stripe to Slack Payment Alerts (8 nodes)
  "Stripe to Slack Payment Alerts": {
    nodes: [
      trigger("Stripe: Payment received", "webhook_app", {
        source: "Stripe",
        event: "payment_intent.succeeded",
        howItStarts: "A customer successfully completes a payment",
        setup:
          "In Stripe → Developers → Webhooks → Add endpoint → URL: your workflow URL → Events: payment_intent.succeeded, charge.refunded",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI reads payment details",
        280,
        "Read this Stripe webhook payload. Extract the payment amount (in dollars, divide cents by 100), customer email, customer name, payment method, currency, and description. Respond JSON: {payment_amount: string, customer_email: string, customer_name: string, payment_method: string, currency: string, payment_description: string}",
        "{webhook_payload}",
        [
          { field: "payment_amount", as: "payment_amount", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "payment_method", as: "payment_method", type: "string" },
          { field: "currency", as: "currency", type: "string" },
          {
            field: "payment_description",
            as: "payment_description",
            type: "string",
          },
        ],
      ),
      n("3", "runCode", "Calculate payment tier", 520, {
        runCodeDetails: {
          code: "const amount = parseFloat(inputs.payment_amount);\nlet tier = 'standard';\nif (amount >= 1000) tier = 'enterprise';\nelse if (amount >= 100) tier = 'pro';\nreturn { payment_tier: tier, amount_formatted: `$${amount.toFixed(2)}` };",
          language: "javascript",
        },
      }),
      n("4", "condition", "Enterprise payment? (>=$1000)", 760, {
        conditionData: [
          {
            variable: "payment_tier",
            value: "enterprise",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "Celebrate in #revenue",
        1000,
        "💰 Payment received!\\n{customer_name} ({customer_email})\\nAmount: {amount_formatted} {currency}\\nTier: {payment_tier}\\nMethod: {payment_method}\\n{payment_description}",
      ),
      slackNode(
        "6",
        "Enterprise → alert #vip-customers",
        1000,
        "🎉 ENTERPRISE PAYMENT: {amount_formatted} from {customer_name}!\\n{customer_email} | {payment_description}\\n@sales congratulations!",
        460,
      ),
      emailNode(
        "7",
        "Thank enterprise customer",
        1240,
        "{customer_email}",
        "Thank you for your purchase, {customer_name}!",
        "<p>Hi {customer_name},</p><p>Thank you for your payment of {amount_formatted} {currency}.</p><p>As a valued enterprise customer, you have access to priority support. Reply to this email anytime you need help.</p>",
        460,
      ),
      n("8", "hitApi", "Log to revenue tracker", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Revenue:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{customer_name}","{customer_email}","{payment_amount}","{currency}","{payment_tier}","{payment_method}","{payment_description}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_added",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("3", "5"),
      e("4", "6"),
      e("4", "7"),
      e("3", "8"),
    ],
    summary: `**Stripe to Slack Payment Alerts** — Full payment intelligence!\n\n1. Stripe sends payment webhook\n2. **AI reads the data** and extracts amount, customer, method, currency\n3. Calculates payment tier (standard/pro/enterprise)\n4. All payments → #revenue Slack celebration\n5. Enterprise ($1000+) → VIP alert + thank-you email\n6. All payments logged to revenue tracker\n\n**To activate:** Say "connect openai", "connect slack", "connect email", "connect google sheets"`,
  },

  // 47. RSS to Social Media (8 nodes)
  "RSS to Social Media": {
    nodes: [
      trigger("Schedule: Every 2 hours", "schedule", {
        schedule: "Every 2 hours",
        howItStarts: "Checks your RSS feed for new blog posts every 2 hours",
        setup:
          "Activate this workflow and set your RSS feed URL in the next node (e.g., https://your-blog.com/feed.xml)",
        inputFields: [
          {
            name: "rss_feed_url",
            type: "string",
            example: "https://your-blog.com/feed.xml",
          },
        ],
      }),
      n("2", "hitApi", "Fetch RSS feed", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.rss2json.com/v1/api.json?rss_url={rss_feed_url}&api_key={rss2json_key}&count=1",
          headers: [],
          variables: [
            {
              name: "items[0].title",
              customName: "post_title",
              type: "string",
            },
            { name: "items[0].link", customName: "post_link", type: "string" },
            {
              name: "items[0].description",
              customName: "post_excerpt",
              type: "string",
            },
            {
              name: "items[0].pubDate",
              customName: "post_date",
              type: "string",
            },
          ],
        },
      }),
      aiNode(
        "3",
        "AI create social posts",
        520,
        "Create social media posts for this blog article. Write a Twitter post (under 280 chars with hashtags), a LinkedIn post (professional, 500 chars with CTA), and an Instagram caption (casual, engaging with emojis and hashtags). Respond JSON: {twitter_post: string, linkedin_post: string, instagram_caption: string, best_hashtags: string}",
        "Title: {post_title}\\nExcerpt: {post_excerpt}\\nLink: {post_link}",
        [
          { field: "twitter_post", as: "twitter_post", type: "string" },
          { field: "linkedin_post", as: "linkedin_post", type: "string" },
          {
            field: "instagram_caption",
            as: "instagram_caption",
            type: "string",
          },
          { field: "best_hashtags", as: "best_hashtags", type: "string" },
        ],
      ),
      n("4", "hitApi", "Post to Twitter/X", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.twitter.com/2/tweets",
          headers: [
            { key: "Authorization", value: "Bearer {twitter_bearer_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "text", value: "{twitter_post}" }],
          variables: [
            { name: "data.id", customName: "tweet_id", type: "string" },
          ],
        },
      }),
      n(
        "5",
        "hitApi",
        "Post to LinkedIn",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.linkedin.com/v2/ugcPosts",
            headers: [
              { key: "Authorization", value: "Bearer {linkedin_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "author", value: "urn:li:person:{linkedin_person_id}" },
              { key: "lifecycleState", value: "PUBLISHED" },
              {
                key: "specificContent",
                value:
                  '{"com.linkedin.ugc.ShareContent":{"shareCommentary":{"text":"{linkedin_post}"},"shareMediaCategory":"ARTICLE","media":[{"status":"READY","originalUrl":"{post_link}"}]}}',
              },
            ],
            variables: [
              { name: "id", customName: "linkedin_post_id", type: "string" },
            ],
          },
        },
        460,
      ),
      n("6", "approvalGate", "Review before Instagram post", 1000, {
        approvalGateData: {
          message:
            "Review Instagram caption:\\n{instagram_caption}\\n\\nHashtags: {best_hashtags}",
        },
      }),
      slackNode(
        "7",
        "Report: posts published",
        1240,
        "📢 Blog shared on social!\\n*{post_title}*\\n\\nTwitter: Posted (ID: {tweet_id})\\nLinkedIn: Posted (ID: {linkedin_post_id})\\nInstagram: Pending approval\\n\\n{post_link}",
      ),
      emailNode(
        "8",
        "Email content team recap",
        1240,
        "content@company.com",
        "Social Posts Published: {post_title}",
        "<h2>{post_title}</h2><p><a href='{post_link}'>View post</a></p><h3>Twitter</h3><p>{twitter_post}</p><h3>LinkedIn</h3><p>{linkedin_post}</p><h3>Instagram (pending)</h3><p>{instagram_caption}</p><p>Hashtags: {best_hashtags}</p>",
        460,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("3", "5"),
      e("3", "6"),
      e("4", "7"),
      e("5", "7"),
      e("4", "8"),
    ],
    summary: `**RSS to Social Media** — Auto-publish across platforms!\n\n1. Checks RSS feed every 2 hours for new posts\n2. AI creates platform-specific content (Twitter, LinkedIn, Instagram)\n3. Auto-posts to Twitter and LinkedIn\n4. Instagram → approval gate for manual review\n5. Slack report + email recap to content team\n\n**To activate:** Say "connect openai", "connect twitter", "connect linkedin", "connect slack", "connect email"`,
  },
};
